import React, { useState, useMemo, useEffect } from 'react';
import { View, TouchableOpacity, TextInput, ActivityIndicator, ScrollView, Image as RNImage } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Ionicons } from '@expo/vector-icons';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import DebtBadge from '../../../../components/DebtBadge';
import Modal from '../../../../components/Modal';
import Toggle from '../../../../components/Toggle';
import { Icon } from '../../../../components/Icon';
import api from '../../../../utils/api';
import { NativeSelect } from './TicketFilters.web';


type TimelineEvent = {
  type: 'created' | 'comment' | 'area_assigned' | 'category_changed' | 'status_changed' | 'priority_changed';
  at: string;
  user_id?: number;
  area_id?: number | null;
  area_name?: string;
  category_name?: string | null;
  from?: string | boolean;
  to?: string | boolean;
  visibility?: string;
  comment_id?: number;
};


export default function TicketDetailModal({ communityId, ticketUuid, areas, categories = [], onRequestClose, onChanged }: any) {
  const queryClient = useQueryClient();
  const [internalMode, setInternalMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const ticketQuery = useQuery<any>({
    queryKey: ['ticket', communityId, ticketUuid],
    queryFn: () => api.get(`/communities/${communityId}/tickets/${ticketUuid}`).then(r => r.data),
    enabled: !!communityId && !!ticketUuid,
  });

  const ticket = ticketQuery.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket', communityId, ticketUuid] });
    onChanged?.();
  };

  const updatePriority = async (is_high_priority: boolean) => {
    try {
      await api.patch(`/communities/${communityId}/tickets/${ticketUuid}/priority`, { is_high_priority });
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al actualizar prioridad');
    }
  };

  const updateStatus = async (status: string) => {
    try {
      await api.patch(`/communities/${communityId}/tickets/${ticketUuid}/status`, { status });
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al actualizar estatus');
    }
  };

  const updateArea = async (area_uuid: string | null) => {
    try {
      await api.put(`/communities/${communityId}/tickets/${ticketUuid}`, { area_uuid });
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al asignar área');
    }
  };

  const updateCategory = async (category_uuid: string | null) => {
    try {
      await api.put(`/communities/${communityId}/tickets/${ticketUuid}`, { category_uuid });
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al cambiar la categoría');
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/communities/${communityId}/tickets/${ticketUuid}/comments`, {
        body: newComment.trim(),
        visibility: internalMode ? 'internal' : 'public',
      });
      setNewComment('');
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al comentar');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Modal onRequestClose={onRequestClose} style={{ modal: tw`w-[1000px]`, content: tw`p-0` }}>
      {!ticket ? (
        <View style={tw`p-10 items-center`}><ActivityIndicator size="small" color="#6366f1" /></View>
      ) : (
        <View style={tw`flex-row h-[85vh]`}>
          <LeftColumn
            ticket={ticket}
            areas={areas}
            categories={categories}
            onAreaChange={updateArea}
            onCategoryChange={updateCategory}
            onPriorityChange={updatePriority}
            onStatusChange={updateStatus}
          />
          <RightColumn
            ticket={ticket}
            newComment={newComment}
            setNewComment={setNewComment}
            internalMode={internalMode}
            setInternalMode={setInternalMode}
            posting={posting}
            submitComment={submitComment}
          />
        </View>
      )}
    </Modal>
  );
}


// ============================================================
// Columna izquierda — folio + seguimiento (área, prioridad, timeline, acción)
// ============================================================

function LeftColumn({ ticket, areas, categories, onAreaChange, onCategoryChange, onPriorityChange, onStatusChange }: any) {
  return (
    <View style={tw`w-[340px] border-r border-gray-200`}>
      <ScrollView contentContainerStyle={tw`p-5`}>
        {/* Header: folio + reportante */}
        <Text style={tw`text-xs text-gray-500 mb-3`}>{ticket.folio}</Text>

        <Text style={tw`text-xs text-gray-500 mb-1`}>
          <Text style={tw`font-500 text-gray-700`}>Reporta: </Text>
          <Text style={tw`text-gray-700`}>{ticket.reporter?.name}</Text>
          {ticket.unit?.name ? <Text style={tw`text-gray-500`}>  -  {ticket.unit.name}</Text> : null}
        </Text>
        <DebtBadge status={ticket.unit?.debt_status} style={tw`mb-2`} />
        <Text style={tw`text-xs text-gray-400 mb-5`}>{dayjs(ticket.created_at).format('D MMM YYYY - H:mm')}</Text>

        <CategoryField ticket={ticket} categories={categories} onChange={onCategoryChange} />

        <AreaField ticket={ticket} areas={areas} onAssign={onAreaChange} />

        {/* Prioridad alta (switch) */}
        <View style={tw`flex-row items-center justify-between border-t border-gray-100 py-4 mb-2`}>
          <Text style={tw`text-sm font-500 text-gray-800`}>Prioridad alta</Text>
          <Toggle
            value={!!ticket.is_high_priority}
            onValueChange={onPriorityChange}
          />
        </View>

        {/* Timeline */}
        <View style={tw`border-t border-gray-100 pt-4`}>
          <Text style={tw`text-sm font-500 text-gray-700 mb-3`}>Status</Text>
          <Timeline ticket={ticket} />
        </View>

        {/* Acción de status */}
        <View style={tw`mt-5`}>
          <StatusAction ticket={ticket} onStatusChange={onStatusChange} />
        </View>
      </ScrollView>
    </View>
  );
}

/**
 * Categoría: se cambia directo con el select (no se elige por accidente). Si la
 * categoría ya no está en el catálogo, se sigue mostrando como opción.
 */
function CategoryField({ ticket, categories, onChange }: any) {
  const current = ticket.category;
  if (categories.length === 0 && !current) return null;

  const options = [
    { value: '', label: 'Sin categoría' },
    ...categories.map((c: any) => ({ value: c.uuid, label: c.name })),
  ];
  if (current && !categories.some((c: any) => c.uuid === current.uuid)) {
    options.push({ value: current.uuid, label: `${current.name} (eliminada)` });
  }

  return (
    <View style={tw`mb-5`}>
      <Text style={tw`text-sm font-500 text-gray-700 mb-2`}>Categoría</Text>
      <NativeSelect
        value={current?.uuid ?? ''}
        onChange={(uuid: string) => onChange(uuid || null)}
        options={options}
      />
    </View>
  );
}

/**
 * Área en dos pasos: el click solo selecciona; se guarda con "Asignar área" /
 * "Transferir a…". Así un click accidental no reasigna ni notifica a nadie.
 */
function AreaField({ ticket, areas, onAssign }: any) {
  const currentUuid = ticket.area?.uuid ?? null;
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Se limpia cuando el ticket ya trae el área nueva (si falla, queda para reintentar).
  useEffect(() => {
    if (pendingUuid && pendingUuid === currentUuid) setPendingUuid(null);
  }, [currentUuid]);

  const pendingArea = areas.find((a: any) => a.uuid === pendingUuid);
  const hasPending = !!pendingUuid && pendingUuid !== currentUuid;

  const confirm = async () => {
    setSaving(true);
    try {
      await onAssign(pendingUuid);
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={tw`mb-5`}>
      <Text style={tw`text-sm font-500 text-gray-700`}>
        Área asignada<Text style={tw`text-red-500`}> *</Text>
      </Text>
      <Text style={tw`text-xs text-gray-500 mb-3`}>
        Elige el área responsable de atender este reporte
      </Text>

      <View style={tw`flex-row flex-wrap gap-2`}>
        {areas.map((a: any) => {
          const isCurrent = currentUuid === a.uuid;
          const isPending = pendingUuid === a.uuid && !isCurrent;
          return (
            <TouchableOpacity
              key={a.uuid}
              onPress={() => setPendingUuid(isCurrent ? null : a.uuid)}
              style={tw.style(
                `px-3 py-1.5 rounded-full border`,
                isCurrent ? 'border-transparent bg-secondary'
                  : isPending ? 'border-secondary bg-secondary-50'
                  : 'border-gray-200 bg-white',
              )}
            >
              <Text style={tw.style(
                `text-xs font-500`,
                isCurrent ? 'text-white' : isPending ? 'text-secondary' : 'text-gray-700',
              )}>
                {a.name}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {hasPending && (
        <View style={tw`flex-row items-center gap-2 mt-3`}>
          <TouchableOpacity
            onPress={confirm}
            disabled={saving}
            style={tw.style(`flex-1 bg-secondary rounded-full py-2 items-center`, saving && 'opacity-50')}
          >
            <Text style={tw`text-white text-sm font-500`} numberOfLines={1}>
              {currentUuid ? `Transferir a ${pendingArea?.name}` : 'Asignar área'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setPendingUuid(null)}
            disabled={saving}
            style={tw`border border-gray-300 rounded-full px-4 py-2`}
          >
            <Text style={tw`text-gray-700 text-sm`}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

function Timeline({ ticket }: any) {
  const events: TimelineEvent[] = ticket.timeline ?? [];
  const items = useMemo(() => buildTimelineItems(events, ticket), [events, ticket.status]);

  return (
    <View>
      {items.map((it, idx) => (
        <View key={idx} style={tw`flex-row mb-3`}>
          {/* Bullet + línea */}
          <View style={tw`w-4 items-center`}>
            <View style={tw.style(
              `w-2.5 h-2.5 rounded-full mt-1.5`,
              it.current ? 'bg-amber-400' : 'bg-emerald-400',
            )} />
            {idx < items.length - 1 && (
              <View style={tw`flex-1 w-px bg-gray-200 mt-1`} />
            )}
          </View>

          <View style={tw`ml-3 flex-1 pb-1`}>
            <View style={tw`flex-row items-center flex-wrap`}>
              <Text style={tw.style(
                `text-sm font-500`,
                it.current ? 'text-amber-600' : 'text-gray-800',
              )}>
                {it.label}
              </Text>
              {it.at && (
                <Text style={tw`ml-2 text-xs text-gray-400`}>
                  {dayjs(it.at).format('D MMM YYYY - H:mm')}
                </Text>
              )}
            </View>
            {it.subline ? (
              <Text style={tw`text-xs text-secondary mt-0.5`}>{it.subline}</Text>
            ) : null}
            {it.subline2 ? (
              <Text style={tw`text-xs text-gray-500`}>{it.subline2}</Text>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function buildTimelineItems(events: TimelineEvent[], ticket: any) {
  const items: Array<{ label: string; at?: string; subline?: string; subline2?: string; current?: boolean }> = [];

  let firstCommentSeen = false;

  for (const e of events) {
    if (e.type === 'created') {
      items.push({ label: 'Reporte recibido', at: e.at });
    } else if (e.type === 'comment') {
      if (!firstCommentSeen) {
        items.push({ label: 'Inicio de Chat', at: e.at });
        firstCommentSeen = true;
      }
    } else if (e.type === 'area_assigned') {
      items.push({
        label: 'Asignado',
        at: e.at,
        subline: (e.area_name as string) ?? 'Sin área',
        subline2: ticket.reporter?.name && e.user_id === ticket.reporter_user_id ? ticket.reporter.name : undefined,
      });
    } else if (e.type === 'category_changed') {
      items.push({
        label: 'Categoría',
        at: e.at,
        subline: e.category_name ?? 'Sin categoría',
      });
    } else if (e.type === 'status_changed') {
      items.push({
        label: statusLabel(e.to as string),
        at: e.at,
      });
    } else if (e.type === 'priority_changed') {
      items.push({
        label: e.to ? 'Marcada como prioridad alta' : 'Prioridad alta removida',
        at: e.at,
      });
    }
  }

  items.push({ label: statusLabel(ticket.status), current: true });

  return items;
}

function statusLabel(status: string) {
  if (status === 'nuevo') return 'Nuevo';
  if (status === 'en_seguimiento') return 'En seguimiento';
  if (status === 'atendida') return 'Atendida';
  return status;
}

function StatusAction({ ticket, onStatusChange }: any) {
  if (ticket.status === 'nuevo') {
    return (
      <TouchableOpacity
        onPress={() => onStatusChange('en_seguimiento')}
        style={tw`bg-primary rounded-full py-2.5 items-center`}
      >
        <Text style={tw`text-white text-sm font-500`}>Iniciar seguimiento</Text>
      </TouchableOpacity>
    );
  }
  if (ticket.status === 'en_seguimiento') {
    return (
      <TouchableOpacity
        onPress={() => onStatusChange('atendida')}
        style={tw`bg-primary rounded-full py-2.5 items-center`}
      >
        <Text style={tw`text-white text-sm font-500`}>Marcar como atendida</Text>
      </TouchableOpacity>
    );
  }
  return (
    <TouchableOpacity
      onPress={() => onStatusChange('en_seguimiento')}
      style={tw`border border-gray-300 rounded-full py-2.5 items-center`}
    >
      <Text style={tw`text-gray-700 text-sm font-500`}>Reabrir reporte</Text>
    </TouchableOpacity>
  );
}


// ============================================================
// Columna derecha — conversación (descripción + imágenes como primeros mensajes)
// ============================================================

function RightColumn({ ticket, newComment, setNewComment, internalMode, setInternalMode, posting, submitComment }: any) {
  const reporterFirstName = (ticket.reporter?.name ?? '').split(' ')[0] || '';

  // Mezcla cronológica de public + internal
  const comments = useMemo(() => {
    const pub = (ticket.public_comments ?? ticket.publicComments ?? []) as any[];
    const internal = (ticket.internal_notes ?? ticket.internalNotes ?? []) as any[];
    return [...pub, ...internal].sort((a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)));
  }, [ticket.public_comments, ticket.publicComments, ticket.internal_notes, ticket.internalNotes]);

  return (
    <View style={tw`flex-1`}>
      <View style={tw`px-5 py-4 border-b border-gray-100 items-center`}>
        <Text style={tw`text-secondary font-600`}>Conversación de la incidencia</Text>
      </View>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`p-5`}>
        {/* Mensaje inicial: descripción del reporte */}
        {ticket.description ? (
          <InitialMessage
            authorName={ticket.reporter?.name}
            at={ticket.created_at}
            body={ticket.description}
          />
        ) : null}

        {/* Mensaje inicial: imágenes (si existen) */}
        {ticket.files?.length > 0 ? (
          <InitialImagesMessage
            authorName={ticket.reporter?.name}
            at={ticket.created_at}
            files={ticket.files}
            hideHeader={!!ticket.description}
          />
        ) : null}

        {/* Comentarios posteriores */}
        {comments.map((c: any) => (
          <CommentBubble
            key={c.uuid}
            comment={c}
            isReporter={!!c.user?.uuid && c.user.uuid === ticket.reporter?.uuid}
          />
        ))}

        {!ticket.description && ticket.files?.length === 0 && comments.length === 0 && (
          <Text style={tw`text-xs text-gray-400`}>Aún no hay comentarios.</Text>
        )}
      </ScrollView>

      <View style={tw`border-t border-gray-100 px-5 py-3`}>
        <TouchableOpacity
          onPress={() => setInternalMode(!internalMode)}
          style={tw`flex-row items-center mb-2`}
        >
          <View style={tw.style(
            `w-4 h-4 border rounded mr-2 items-center justify-center`,
            internalMode ? 'bg-primary border-transparent' : 'border-gray-300 bg-white',
          )}>
            {internalMode && <Ionicons name="checkmark" size={12} color="white" />}
          </View>
          <Text style={tw`text-xs text-gray-600`}>Comentario interno (solo admin)</Text>
        </TouchableOpacity>

        <View style={tw`flex-row items-end gap-2`}>
          <TextInput
            value={newComment}
            onChangeText={setNewComment}
            placeholder={internalMode ? 'Nota interna…' : `Escribe a ${reporterFirstName || 'el reportante'}`}
            placeholderTextColor="#9ca3af"
            multiline
            style={tw`flex-1 bg-gray-100 border border-gray-200 rounded-lg px-4 py-1.5 text-sm max-h-16`}
          />
          <TouchableOpacity
            onPress={submitComment}
            disabled={posting || !newComment.trim()}
            style={tw.style(
              `w-8 h-8 rounded-full items-center justify-center`,
              !newComment.trim() ? 'bg-gray-200' : 'bg-primary',
            )}
          >
            <Icon name="send" size={14} color={!newComment.trim() ? tw.color('gray-400') : 'white'} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

function InitialMessage({ authorName, at, body }: any) {
  return (
    <View style={tw`mb-4 items-start`}>
      <Text style={tw`text-xs text-gray-500 mb-1 px-1`}>
        {authorName} - {dayjs(at).format('D MMM H:mm')}
      </Text>
      <View style={tw`rounded-xl px-3 py-2 max-w-[85%] bg-[#F0FEFA] border border-[#B8F4EE]`}>
        <Text style={tw`text-sm text-gray-800`}>{body}</Text>
      </View>
    </View>
  );
}

function InitialImagesMessage({ authorName, at, files, hideHeader }: any) {
  const openImage = (url: string) => {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <View style={tw`mb-4 items-start`}>
      {!hideHeader && (
        <Text style={tw`text-xs text-gray-500 mb-1 px-1`}>
          {authorName} - {dayjs(at).format('D MMM H:mm')}
        </Text>
      )}
      <View style={tw`flex-row flex-wrap gap-2`}>
        {files.map((f: any) => (
          <TouchableOpacity key={f.uuid} onPress={() => openImage(f.url)}>
            <RNImage
              source={{ uri: f.url }}
              style={tw`w-24 h-24 rounded-lg bg-gray-200`}
            />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

function CommentBubble({ comment, isReporter }: any) {
  const isInternal = comment.visibility === 'internal';
  const alignRight = !isReporter; // admin/staff a la derecha; reporter a la izquierda

  const bubbleColor = isInternal
    ? 'bg-amber-100 border border-amber-200'
    : alignRight
      ? 'bg-[#FAF3FF] border border-[#F1DFFB]'
      : 'bg-[#F0FEFA] border border-[#B8F4EE]';

  return (
    <View style={tw.style(`mb-4`, alignRight ? 'items-end' : 'items-start')}>
      <Text style={tw.style(`text-xs text-gray-500 mb-1 px-1`, alignRight ? 'self-end' : 'self-start')}>
        {comment.user?.name} - {dayjs(comment.created_at).format('D MMM H:mm')}
        {isInternal ? ' · interno' : ''}
      </Text>
      <View style={tw.style(`rounded-xl px-3 py-2 max-w-[85%]`, bubbleColor)}>
        <Text style={tw`text-sm text-gray-800`}>{comment.body}</Text>
      </View>
    </View>
  );
}
